'use client'

import { useEffect, useState } from 'react'
import { Button, Cell, Divider, Dialog, Navigation, Spacer } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import { useSession } from '@marketplace-web/shared/session'
import {
  updateUserBundleDiscounts,
  BundleDiscountModel,
  UserBundleDiscountDto,
} from '@marketplace-web/bundles/items-bundle-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import { BundleDiscountsSettingsState } from '../../BundleDiscountsSettings/types'
import BundleDiscountsToggle from '../../BundleDiscountsSettings/BundleDiscountsToggle'
import useFetchInitialBundleDiscountsSettings from '../../BundleDiscountsSettings/hooks/useFetchInitialBundleDiscountsSettings'

import BundleDiscountsSelectionList from './BundleDiscountsSelectionList'

type Props = {
  show: boolean
  onClose: () => void
  bundleDiscount?: UserBundleDiscountDto | null
  onBundleDiscountUpdate: (bundleDiscount?: UserBundleDiscountDto | null) => void
}

const BundleDiscountsModal = ({ show, onClose, bundleDiscount, onBundleDiscountUpdate }: Props) => {
  const trackAbTest = useTrackAbTestCallback()
  const { user } = useSession()
  const userId = user?.id || null

  const translate = useTranslate('item_upload.price.bundle_discounts_hint.modal')
  const initialSettings = useFetchInitialBundleDiscountsSettings()

  const [isUpdating, setIsUpdating] = useState(false)
  const [settings, setSettings] = useState<BundleDiscountsSettingsState>(initialSettings)

  const confirmButtonAbTest = useAbTest('dd_tx_web_remove_bundle_discounts_modal_confirm_button')
  const isConfirmButtonEnabled = confirmButtonAbTest?.variant !== 'on'

  useEffect(() => {
    if (show && confirmButtonAbTest) {
      trackAbTest(confirmButtonAbTest)
    }
  }, [show, trackAbTest, confirmButtonAbTest])

  useEffect(() => {
    if (initialSettings.isLoading) return

    setSettings(initialSettings)
  }, [initialSettings])

  const updateDiscounts = async ({
    isEnabled,
    selectedDiscounts,
  }: {
    isEnabled: boolean
    selectedDiscounts: Array<BundleDiscountModel>
  }) => {
    if (!userId) return

    setIsUpdating(true)

    await updateUserBundleDiscounts({
      userId,
      isEnabled,
      discounts: selectedDiscounts,
    })

    const updatedBundleDiscount = bundleDiscount
      ? {
          ...bundleDiscount,
          enabled: isEnabled,
          discounts: selectedDiscounts.map(discount => ({
            fraction: discount.fraction,
            minimal_item_count: discount.minimalItemCount,
          })),
        }
      : null
    onBundleDiscountUpdate(updatedBundleDiscount)

    setIsUpdating(false)
  }

  function handleDiscountsToggleChange(isChecked: boolean) {
    setSettings(prevSettings => ({
      ...prevSettings,
      isDiscountEnabled: isChecked,
    }))

    updateDiscounts({
      isEnabled: isChecked,
      selectedDiscounts: settings.selectedDiscounts,
    })
  }

  const isDiscountInIncreasingOrder = (
    discountToValidate: BundleDiscountModel,
    referenceDiscount: BundleDiscountModel,
  ) => {
    const fractionToValidate = Number(discountToValidate.fraction)
    const fractionOfReference = Number(referenceDiscount.fraction)

    const isDiscountValueLower = fractionOfReference < fractionToValidate
    const isDiscountValueBigger = fractionOfReference > fractionToValidate

    const isPositionAbove = discountToValidate.minimalItemCount < referenceDiscount.minimalItemCount
    const isPositionBelow = discountToValidate.minimalItemCount > referenceDiscount.minimalItemCount

    return (isPositionAbove && isDiscountValueBigger) || (isPositionBelow && isDiscountValueLower)
  }

  const getDiscountsOverridenInIncreasingOrder = (referenceDiscount: BundleDiscountModel) => {
    return settings.selectedDiscounts.map(discount => {
      if (discount.minimalItemCount === referenceDiscount.minimalItemCount) return referenceDiscount
      if (isDiscountInIncreasingOrder(discount, referenceDiscount)) return discount

      return {
        ...discount,
        fraction: referenceDiscount.fraction,
      }
    })
  }

  const handleDiscountSelection = (updatedDiscount: BundleDiscountModel) => {
    const selectedDiscounts = getDiscountsOverridenInIncreasingOrder(updatedDiscount)

    setSettings(prevSettings => ({
      ...prevSettings,
      selectedDiscounts,
    }))

    updateDiscounts({
      selectedDiscounts,
      isEnabled: settings.isDiscountEnabled,
    })
  }

  const renderLoader = () => (
    <ContentLoader size={ContentLoader.Size.Large} testId="bundle-discounts-hint-modal-loader" />
  )

  const renderContent = () => (
    <>
      <BundleDiscountsToggle
        isDisabled={isUpdating}
        isChecked={settings.isDiscountEnabled}
        onChange={handleDiscountsToggleChange}
      />
      <Divider />
      {settings.isDiscountEnabled && (
        <Cell testId="bundle-discounts-selection-list" styling={Cell.Styling.Tight}>
          <Spacer />
          <BundleDiscountsSelectionList
            isDisabled={isUpdating}
            selectedDiscounts={settings.selectedDiscounts}
            discountFractions={settings.availableDiscountFractions}
            onDiscountSelection={handleDiscountSelection}
          />
        </Cell>
      )}
    </>
  )

  return (
    <Dialog show={show} testId="bundle-discounts-hint-modal">
      <Navigation
        body={translate('title')}
        right={
          <Button
            inline
            iconName={X24}
            styling={Button.Styling.Flat}
            onClick={onClose}
            aria={{ 'aria-label': translate('a11y.close') }}
            testId="bundle-discounts-hint-modal-close-button"
          />
        }
      />
      <Divider />
      {settings.isLoading ? renderLoader() : renderContent()}
      {isConfirmButtonEnabled ? (
        <Cell>
          <Button
            text={translate('actions.confirm')}
            styling={Button.Styling.Filled}
            onClick={onClose}
            testId="bundle-discounts-modal-confirm-button"
          />
        </Cell>
      ) : (
        <Spacer />
      )}
    </Dialog>
  )
}

export default BundleDiscountsModal
